import { CACHE_TIMES } from '@/lib/queryClient'
import { supabase } from '@/lib/supabase'
import { I18N_FIELDS, localizeRows } from '@/i18n/localize'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

/** Minimal activity shape needed to render a link. */
export type NavActivity = {
  id: string
  title: string
  slug: string
  activity_type: string
  i18n?: Record<string, Record<string, unknown>> | null
}

/**
 * Where an activity card/menu entry points. A few activities have a dedicated
 * lander that outranks their generic /activities/:slug page, so the rule lives
 * here and is shared by the home grid and the header menu.
 */
export function activityPath(a: Pick<NavActivity, 'slug' | 'activity_type'>) {
  if (a.activity_type === 'enduro' || a.slug === 'enduro-tours') return '/tours'
  if (a.slug === 'paramotor-tandem-flight') return '/paramotor-vang-vieng'
  if (a.activity_type === 'rock_climbing') return '/rock-climbing'
  if (a.activity_type === 'trekking') return '/trek'
  // /activities/:slug 301s to the singular form — link the canonical directly.
  return `/activity/${a.slug}`
}

/**
 * Active activities for the header menu, in display order, with the two rock
 * climbing products collapsed into one entry (both are booked on
 * /rock-climbing). Trekking is excluded — it has its own /trek listing.
 */
export function useNavActivities() {
  const { i18n } = useTranslation()
  return useQuery({
    queryKey: ['activities', 'nav', i18n.language],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('activities')
        .select('id, title, slug, activity_type, i18n')
        .eq('is_active', true)
        .neq('activity_type', 'trekking')
        .order('display_order', { ascending: true })

      if (error) throw error
      const rows = localizeRows(
        (data ?? []) as NavActivity[],
        i18n.language,
        I18N_FIELDS.activities
      )

      let climbingShown = false
      return rows.flatMap((a) => {
        if (a.activity_type !== 'rock_climbing') return [a]
        if (climbingShown) return []
        climbingShown = true
        return [{ ...a, title: 'Rock Climbing' }]
      })
    },
    ...CACHE_TIMES.STATIC,
  })
}
